import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import logo from '@/assets/images/nebi-logo.png';
import styles from './PWAInstallPrompt.module.scss';

// Define the BeforeInstallPromptEvent interface
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  const [isIOS] = useState(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isStandalone =
      ('standalone' in window.navigator && (window.navigator as any).standalone) ||
      window.matchMedia('(display-mode: standalone)').matches;
    return isIosDevice && !isStandalone;
  });

  const [showPrompt, setShowPrompt] = useState(() => {
    // For iOS, check immediately
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isStandalone =
      ('standalone' in window.navigator && (window.navigator as any).standalone) ||
      window.matchMedia('(display-mode: standalone)').matches;

    if (isIosDevice && !isStandalone) {
      const hasDismissed = localStorage.getItem('pwa-prompt-dismissed');
      return !hasDismissed;
    }
    return false;
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Update UI notify the user they can install the PWA
      // We check if the user has already dismissed it recently directly in state logic if needed,
      // but for now we just show it.
      // Optional: Check local storage to see if user dismissed it recently
      const hasDismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!hasDismissed) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // iOS doesn't support programmatic install, so we just close the modal
      // The UI already shows instructions
      handleClose();
      return;
    }

    if (!deferredPrompt) return;

    // Hide the app provided install promotion
    setShowPrompt(false);

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    // Optionally log the outcome
    console.log(`User response to the install prompt: ${outcome}`);

    // We've used the prompt, and can't use it again, throw it away
    setDeferredPrompt(null);
  };

  const handleClose = () => {
    setShowPrompt(false);
    // Remember the user's choice to dismiss
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <Modal
      isOpen={showPrompt}
      onClose={handleClose}
      title="Instalar Aplicativo"
      className={styles.installModal}
    >
      <div className={styles.container}>
        <div className={styles.logoWrapper}>
          <img src={logo} alt="NEBI App" className={styles.appLogo} />
        </div>

        <p className={styles.description}>
          {isIOS ? (
            <>
              Para instalar o <strong>NEBI</strong> no seu iPhone/iPad:
              <br />
              <br />
              1. Toque no botão de <strong>Compartilhar</strong>{' '}
              <span style={{ fontSize: '1.2em' }}>⎋</span>
              <br />
              2. Selecione <strong>Adicionar à Tela de Início</strong>{' '}
              <span style={{ fontSize: '1.2em' }}>➕</span>
            </>
          ) : (
            <>
              Deseja adicionar o <strong>NEBI</strong> à sua tela inicial para um acesso mais
              rápido, fácil e uma melhor experiência?
            </>
          )}
        </p>

        <div className={styles.actions}>
          <Button variant="outlined" onClick={handleClose} type="button">
            {isIOS ? 'Entendi' : 'Agora não'}
          </Button>
          {!isIOS && (
            <Button variant="primary" onClick={handleInstallClick} type="button" icon="download">
              Instalar
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
